import { z } from 'zod';

import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

import { operatorApiFetch } from './apiService';

// ============================================================
// Schemas — 對齊 app/controllers/api/operator/v1/merchant_inventory_types_controller.rb
// ============================================================

const InventoryTypeRefSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  full_name: z.string(),
}).passthrough();

// `index` (render_array_list) — merchant_name 只在不指定 merchant_id 時才出現
const MerchantInventoryTypeListItemSchema = z.object({
  id: z.string(),
  merchant_id: z.string(),
  inventory_type_id: z.string(),
  inventory_type: InventoryTypeRefSchema,
  expiration_mechanism: z.string().nullable(),
  expiration_ratio: z.number().nullable(),
  days_before_expiration: z.number().nullable(),
  expiration_notification_enabled: z.boolean().nullable(),
  expiration_notification_days: z.number().nullable(),
  ignore_sales_channel: z.boolean().nullable(),
  inventory_age_alert_days: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  merchant_name: z.string().optional(),
}).passthrough();

// `create` / `update` 的 merchant_inventory_type_response — 與 index 同 shape，
// 但沒有 merchant_name 欄位
const MerchantInventoryTypeDetailSchema = z.object({
  id: z.string(),
  merchant_id: z.string(),
  inventory_type_id: z.string(),
  inventory_type: InventoryTypeRefSchema,
  expiration_mechanism: z.string().nullable(),
  expiration_ratio: z.number().nullable(),
  days_before_expiration: z.number().nullable(),
  expiration_notification_enabled: z.boolean().nullable(),
  expiration_notification_days: z.number().nullable(),
  ignore_sales_channel: z.boolean().nullable(),
  inventory_age_alert_days: z.number().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
}).passthrough();

// `destroy` → render_success（無 data） → `{ success: true, message, data: null }`
const NullDataSchema = z.null();

// ============================================================
// Exported types
// ============================================================

export type MerchantInventoryTypeListItem = z.infer<typeof MerchantInventoryTypeListItemSchema>;
export type MerchantInventoryTypeDetail = z.infer<typeof MerchantInventoryTypeDetailSchema>;
export type InventoryTypeRef = z.infer<typeof InventoryTypeRefSchema>;

// ============================================================
// Service
// ============================================================

interface FetchListParams {
  merchantId?: string;
  since_id?: string;
  page_size?: number;
  [key: string]: unknown;
}

interface MutationContext {
  merchantId?: string;
}

export const merchantInventoryTypeService = {
  // 列表
  fetchList: async (params: FetchListParams = {}) => {
    const { merchantId, ...otherParams } = params;
    if (!merchantId) throw new Error('Merchant ID is required');

    const queryParams = new URLSearchParams();
    if (otherParams.since_id) queryParams.append('since_id', String(otherParams.since_id));
    if (otherParams.page_size) queryParams.append('page_size', String(otherParams.page_size));
    // 可添加其他過濾參數

    const response = await operatorApiFetch(`merchants/${merchantId}/merchant_inventory_types?${queryParams.toString()}`, { method: 'GET' });
    return parseApiResponse(response, apiList(MerchantInventoryTypeListItemSchema));
  },

  // 新增
  create: async (data: Record<string, unknown>, params: MutationContext = {}) => {
    const { merchantId } = params;
    if (!merchantId) throw new Error('Merchant ID is required');
    const response = await operatorApiFetch(`merchants/${merchantId}/merchant_inventory_types`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return parseApiResponse(response, apiSuccess(MerchantInventoryTypeDetailSchema));
  },

  // 更新
  update: async (id: string, data: Record<string, unknown>, params: MutationContext = {}) => {
    const { merchantId } = params;
    if (!merchantId) throw new Error('Merchant ID is required');
    const response = await operatorApiFetch(`merchants/${merchantId}/merchant_inventory_types/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return parseApiResponse(response, apiSuccess(MerchantInventoryTypeDetailSchema));
  },

  // 刪除
  delete: async (id: string, params: MutationContext = {}) => {
    const { merchantId } = params;
    if (!merchantId) throw new Error('Merchant ID is required');
    const response = await operatorApiFetch(`merchants/${merchantId}/merchant_inventory_types/${id}`, {
      method: 'DELETE',
    });
    return parseApiResponse(response, apiSuccess(NullDataSchema));
  },
};
export default merchantInventoryTypeService;
